import React, { createRef, FC, RefObject, useMemo, useRef } from 'react';
import { useTranslation } from 'react-i18next';
import classNames from 'classnames';

import Copy from 'Src/components/Copy';
import FontIcon from 'Src/components/icons/FontIcon';
import { Flex } from 'Src/components/layout/Flex';
import { useScrollShadow } from 'Src/components/useScrollShadow';
import { useSnapScroll } from 'Src/components/useSnapScroll';
import { Divider } from 'Src/feature/divider/Divider';

import styles from './CheckItemDetailSeatPosition.module.scss';

const sectionId = 'CheckItemDetailSeatPosition';
const numOfSeats = 99;

const seatNameSort = (a: string, b: string) => {
  const re = /\D/g;
  const aNum = parseInt(a.replace(re, ''));
  const bNum = parseInt(b.replace(re, ''));
  return aNum - bNum;
};

// NOTE: The names of seats are "kinda" available from the back end, but we don't use them in this component.
//       I say kinda, because the backend DOES return the name of seats, but there is no way to set these values.
//       So it is always just "Seat 1", "Seat 2", etc. The product team wanted the names to be S1, S2, etc, AND
//       they wanted it go up to 99. So we just ignore the seat names for now.
//       If we pull in seat names into this component in the future, there are two things we should think about:
//        1. If seat names can be long, we need to make sure that the size of the buttons for the seats looks correct.
//           So for example maybe we need to use text ellipses or something for long names.
//        2. What happens if a check is transferred from one table to another and now names of seats don't match
//           on the new home for the check?
interface CheckItemDetailSeatPositionProps {
  label: string | undefined;
  value: string[];
  onChange: (next: string[]) => void;
  ['data-card-id']: string;
}
const CheckItemDetailSeatPosition: FC<CheckItemDetailSeatPositionProps> = (props) => {
  const { t } = useTranslation();
  const { label, value, onChange } = props;

  const optionsContainerRef = useRef<HTMLDivElement>(null);
  useScrollShadow({ targetRef: optionsContainerRef, mode: 'horizontal' });

  const tableBtnRef = useRef<HTMLButtonElement>(null);
  const seatBtnRefs = useMemo(() => {
    const result: RefObject<HTMLButtonElement>[] = [];
    for (let i = 0; i < numOfSeats; i++) result.push(createRef() as RefObject<HTMLButtonElement>);
    return result;
  }, []);
  const allOptionRefs = useMemo(() => [tableBtnRef, ...seatBtnRefs], [seatBtnRefs]);
  useSnapScroll(optionsContainerRef, allOptionRefs, 'horizontal');

  // only show selected state for the "main" table button when not required and no selections
  // FYI: removing seatNames form validation but keeping for reference
  // const tableSelected = !checkItemBuilder.isSeatNamesRequired && value.length === 0;
  // - this will require backend changes to work properly if we want to pull this back in
  // - "seatNames" will need to come from the api null/undefined or an array
  const tableSelected = value.length === 0;

  const handleTableClick = () => onChange([]);
  const handleSeatClick = (seatName: string) => {
    if (value.includes(seatName)) onChange(value.filter((val) => val !== seatName));
    else onChange([...value, seatName].sort(seatNameSort));
  };

  return (
    <Flex
      data-testid={sectionId}
      gap="sm"
      className={styles['root']}
      data-card-id={props['data-card-id']}
    >
      <Divider variant="stubby" />
      <div className={styles.headingRoot}>
        <Copy
          className={styles.headingText}
          value={t('labels.seatAssignment')}
          color="textPrimary"
        />
        <Copy
          className={styles.headingText}
          value={t('labels.util.wrappedWithParenthesis', { value: label })}
          color="textSecondary"
        />
      </div>
      <div className={styles.optionsContainer} ref={optionsContainerRef}>
        <button
          className={classNames(styles.btn, styles.tableBtn, { [styles.selected]: tableSelected })}
          onClick={handleTableClick}
          ref={tableBtnRef}
        >
          <Copy className={styles.label} value={t('labels.table')}/>
          {tableSelected && <FontIcon key="done" name="check" size="sm" />}
        </button>
        {Array.from({ length: numOfSeats }, (v, k) => `S${k+1}`).map((seatName, index) => {
          const selected = value.includes(seatName);
          return (
            <button
              className={classNames(styles.btn, styles.seatBtn, { [styles.selected]: selected })}
              onClick={() => handleSeatClick(seatName)}
              key={seatName}
              ref={seatBtnRefs[index]}
            >
              <Copy className={classNames(styles.label, styles.seatBtnLabel)} value={seatName}/>
              {selected && <FontIcon className={styles.seatBtnIcon} key="done" name="check" size="sm" />}
            </button>
          );
        })}
      </div>
    </Flex>
  );
};

export { CheckItemDetailSeatPosition, sectionId };
